/**
 *
 * Overview
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Alert, ListGroup, Card } from 'react-bootstrap';
import PrintButton from 'components/interaction/PrintButton/index.jsx';
import Switch from 'components/interaction/Switch/index.jsx';
import Spinner from 'components/Spinner/index.jsx';
import BackButton from 'components/interaction/BackButton/index.jsx';
import FormattedDate from 'components/FormattedDate/index.jsx';
import { idValues } from 'utils/arrays.js';
import messages from './messages.js';
import { SLOT_ASSIGNED, SLOT_BLOCKED, SLOT_BREAK, SLOT_CLASSNAMES } from '../constants.js';
import MeetingInfo from './MeetingInfo.jsx';

const BlockPanel = styled(Card)`
  h3 {
    line-height: 24px;
  }
  @media print {
    break-inside: avoid;
  }
`;

const ListGroupRow = styled(ListGroup.Item)`
  .btn.btn {
    padding-top: 0;
    padding-bottom: 0;
  }
  &.slot-break, &.slot-blocked {
    opacity: 0.7;
  }
  display: flex;
  .col-sm {
    flex: 0 0 6em;
  }
  .col-names {
    flex: 0 0 15em;
  }
  .col-location {
    flex: 0 0 18em;
    display: flex;
    align-items: baseline;
  }
  .col-parents {
    flex-grow: 1;
  }
  @media print {
    .col-location {
      flex-basis: 12em;
    }
  }
`;

function Overview(props) {
  const { groupId, meetingSets } = props;
  const [showEmpty, setShowEmpty] = useState(false);
  const { mid } = useParams();
  const setId = Number(mid);

  if (meetingSets.loading) {
    return <Spinner />;
  }
  if (meetingSets.error) {
    return <span>ERROR</span>;
  }
  const selectedSets = setId ? [meetingSets[setId]] : idValues(meetingSets);
  const blocks = selectedSets
    .flatMap((o) => idValues(o.blocks)
      .map((b) => ({ ...b, title: o.title })))
    .map((b) => ({ ...b, slots: showEmpty ? b.timeslots : b.timeslots.filter((o) => o.status === SLOT_ASSIGNED) }));
  const parentLink = `/groups/${groupId}/meetings`;
  const header = setId
    ? <FormattedMessage {...messages.title} values={{ title: meetingSets[setId]?.title }} />
    : <FormattedMessage {...messages.header} />;

  return (
    <Card className="mb-0 border-0">
      <Card.Header className="d-flex align-items-center">
        <BackButton variant="link" to={parentLink} className="py-0 d-print-none ms-n3" />
        <h2 className="card-title flex-grow-1">
          {header}
          <PrintButton className="ms-3 d-print-none" iconClass="fs-2 lower-1" />
        </h2>
        <Switch value={showEmpty} onChange={() => setShowEmpty(!showEmpty)} before className="d-print-none">
          <FormattedMessage {...messages.showEmpty} />
        </Switch>
      </Card.Header>
      <Card.Body>
        {blocks.length === 0
          ? <Alert variant="info"><FormattedMessage {...messages.noMeetings} /></Alert>
          : blocks.map((blk) => (
            <BlockPanel key={blk.id} className="border">
              <Card.Header className="border-bottom pb-3">
                <h3 className="d-flex justify-content-between m-0">
                  <FormattedDate format="LLL">{blk}</FormattedDate>
                  {!setId && <span className="block-title">{blk.title}</span>}
                </h3>
              </Card.Header>
              <ListGroup>
                {blk.slots.length === 0
                  ? <ListGroup.Item><FormattedMessage {...messages.noSlotsAssigned} /></ListGroup.Item>
                  : blk.slots.map((slot) => (
                    <ListGroupRow key={slot.id} className={SLOT_CLASSNAMES[slot.status]}>
                      <div className="col-sm text-nowrap"><FormattedDate format="LT">{slot}</FormattedDate></div>
                      {slot.status === SLOT_BREAK && <FormattedMessage {...messages.break} />}
                      {slot.status === SLOT_BLOCKED && <FormattedMessage {...messages.blocked} />}
                      {slot.status === SLOT_ASSIGNED && <MeetingInfo meeting={slot.meeting} />}
                    </ListGroupRow>
                  ))}
              </ListGroup>
            </BlockPanel>
          ))}
      </Card.Body>
    </Card>
  );
}

Overview.propTypes = {
  groupId: PropTypes.number,
  setId: PropTypes.number,
  meetingSets: PropTypes.object,
};

export default Overview;
